import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { JudgeKindIcon } from '@/components/atoms/JudgeKindIcon';
import { useModuleFilter } from '@/context/ModuleFilter';
import type { Judge, JudgeKind } from '@/schemas/judges';

import { JudgesLoading } from './Judges.loading';
import { useJudges, useToggleJudge } from './Judges.requests';
import { styles } from './Judges.styles';

const formatUpdated = (iso: string): string => {
	const d = new Date(iso);

	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const Judges = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'judges' });
	const navigate = useNavigate();
	const { selectedModule } = useModuleFilter();
	const [search, setSearch] = useState('');
	const [kindFilter, setKindFilter] = useState<JudgeKind | 'all'>('all');
	const { data, isLoading, isError } = useJudges({ moduleName: selectedModule });
	const toggleMutation = useToggleJudge();

	if (isLoading) return <JudgesLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const filtered = data.items.filter((j) => {
		const matchesSearch = search
			? j.name.toLowerCase().includes(search.toLowerCase()) ||
				j.description.toLowerCase().includes(search.toLowerCase())
			: true;
		const matchesKind = kindFilter === 'all' || j.kind === kindFilter;

		return matchesSearch && matchesKind;
	});

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
				<Link
					to="/judges/new"
					className="inline-flex items-center gap-3xs rounded-md bg-purple-500 px-md py-3xs text-[12px] font-medium text-white transition-colors hover:bg-purple-700"
				>
					<Plus className="h-sm w-sm" />
					{t('NEW')}
				</Link>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>{t('SUBTITLE')}</p>
					</div>
					<div className="flex items-center gap-xs">
						<div className="relative w-[288px]">
							<Search className="pointer-events-none absolute left-xs top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-subtle" />
							<input
								type="search"
								placeholder={t('SEARCH_PLACEHOLDER')}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="w-full rounded-md border border-subtle bg-white px-xs py-2xs pl-xl text-[13px] text-default placeholder:text-subtle focus:border-action-primary focus:outline-none focus:ring-2 focus:ring-action-primary/15"
							/>
						</div>
						<select
							value={kindFilter}
							onChange={(e) => setKindFilter(e.target.value as JudgeKind | 'all')}
							className="rounded-md border border-subtle bg-white px-xs py-2xs text-[13px] text-default focus:outline-none"
						>
							<option value="all">{t('FILTER_ALL_KINDS')}</option>
							<option value="rubric">{t('KIND_RUBRIC')}</option>
							<option value="checklist">{t('KIND_CHECKLIST')}</option>
						</select>
					</div>
				</header>

				{filtered.length === 0 ? (
					<section className="flex flex-col items-start gap-xs rounded-md border border-subtle bg-grey-25 px-xl py-xl">
						<p className="text-[14px] font-semibold text-default">{t('EMPTY_TITLE')}</p>
						<p className="text-[12px] text-subtle">{t('EMPTY_BODY')}</p>
					</section>
				) : (
					<section className="overflow-x-auto rounded-md border border-subtle bg-white">
						<table className="min-w-full text-left text-[13px]">
							<thead className="border-b border-subtle bg-grey-25 text-[11px] font-medium uppercase tracking-wider text-subtle">
								<tr>
									<th className="px-md py-xs">{t('COL_NAME')}</th>
									<th className="px-md py-xs">{t('COL_KIND')}</th>
									<th className="px-md py-xs">{t('COL_APPLIES')}</th>
									<th className="px-md py-xs">{t('COL_STATUS')}</th>
									<th className="px-md py-xs">{t('COL_UPDATED')}</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((j) => (
									<JudgeRow
										key={j.id}
										judge={j}
										onClick={() => navigate(`/judges/${j.id}`)}
										onToggle={() => toggleMutation.mutate({ id: j.id })}
									/>
								))}
							</tbody>
						</table>
					</section>
				)}
			</main>
		</>
	);
};

type JudgeRowProps = {
	judge: Judge;
	onClick: () => void;
	onToggle: () => void;
};

const JudgeRow = ({ judge, onClick, onToggle }: JudgeRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'judges' });

	const handleToggleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onToggle();
	};

	const handleToggleKey = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			onToggle();
		}
	};

	const moduleCount = judge.appliesTo.modules.length;
	const agentCount = judge.appliesTo.agents.length;

	return (
		<tr onClick={onClick} className="group cursor-pointer border-b border-subtle/50 transition-colors hover:bg-grey-25">
			<td className="px-md py-md">
				<p className="text-[13px] font-semibold text-default">{judge.name}</p>
				<p className="mt-3xs text-[11px] text-subtle">{judge.description}</p>
			</td>
			<td className="px-md py-md">
				<span className="inline-flex items-center gap-3xs text-[12px] text-default">
					<JudgeKindIcon kind={judge.kind} />
					{judge.kind === 'rubric' ? t('KIND_RUBRIC') : t('KIND_CHECKLIST')}
				</span>
			</td>
			<td className="px-md py-md text-[12px] text-subtle">
				{moduleCount === 0 && agentCount === 0
					? t('APPLIES_ALL')
					: t('APPLIES_MODULES_AGENTS', { modules: moduleCount, agents: agentCount })}
			</td>
			<td className="px-md py-md">
				<span
					role="switch"
					tabIndex={0}
					aria-checked={judge.enabled}
					aria-label={judge.enabled ? t('STATUS_ENABLED') : t('STATUS_DISABLED')}
					onClick={handleToggleClick}
					onKeyDown={handleToggleKey}
					className={`inline-flex cursor-pointer items-center gap-3xs rounded-full px-2xs py-3xs text-[11px] font-medium transition-colors ${
						judge.enabled ? 'bg-green-50 text-success' : 'bg-grey-25 text-subtle'
					}`}
				>
					<span className={`inline-block h-2xs w-2xs rounded-full ${judge.enabled ? 'bg-green-500' : 'bg-grey-200'}`} />
					{judge.enabled ? t('STATUS_ENABLED') : t('STATUS_DISABLED')}
				</span>
			</td>
			<td className="px-md py-md text-[12px] tabular-nums text-subtle">{formatUpdated(judge.updatedAt)}</td>
		</tr>
	);
};
